import { useMemo } from "react";
import { t } from "i18next";
import { Trans } from "react-i18next";
import { MdInfoOutline } from "react-icons/md";

import { SvText } from "@/components";
import { SvDialogBasic } from "@/components/SvDialog/SvDialogBasic.tsx";
import { PartyMetaType } from "@/domain/models.tsx";
import { I18nKeyType } from "@/i18n";
import { cn } from "@/utils/tailwind.ts";

type SvCandidateBlockProps = {
  type: "trending" | "vote_for" | "incumbent";
  party: PartyMetaType;
  className?: string;
};

export const SvCandidateBlock = ({
  type,
  party,
  className,
}: SvCandidateBlockProps) => {
  const label: I18nKeyType = useMemo(() => {
    if (type === "vote_for") return "projections.vote_for";
    if (type === "trending") return "projections.trending";
    return "projections.incumbent";
  }, [type]);

  return (
    <div
      className={cn(
        "flex w-full flex-col items-center justify-center gap-2 rounded-lg p-2 sm:w-[220px] md:h-32",
        {
          "border-ind-grey-50 border":
            type === "incumbent" || type === "trending",
          "border-ind-grey-600 bg-ind-grey-40 border": type === "vote_for",
        },
        className,
      )}>
      <SvText
        i18nKey={label}
        className={cn("text-ind-grey-400 leading-6 font-medium", {
          "text-ind-grey-600": type === "vote_for",
        })}>
        {type === "vote_for" && (
          <SvDialogBasic
            trigger={
              <MdInfoOutline className="ml-1 inline-block cursor-pointer align-[-2px]" />
            }
            titleKey="projections.why_vote_for.title">
            <Trans i18nKey="projections.why_vote_for.content" />
          </SvDialogBasic>
        )}
        {type === "incumbent" && (
          <SvDialogBasic
            trigger={
              <MdInfoOutline className="ml-1 inline-block cursor-pointer align-[-2px]" />
            }
            titleKey="projections.incumbent_desc.title">
            <SvText
              i18nKey="projections.incumbent_desc.content"
              i18nValues={{ link: "https://elections.ca" }}
            />
          </SvDialogBasic>
        )}
      </SvText>

      <div className="flex flex-row items-center justify-center gap-2 text-lg font-semibold">
        <span
          className="inline-block size-5 rounded-sm"
          style={{ backgroundColor: `#${party.color}` }}></span>
        {t(party.nameKey)}
      </div>
      {party.candidate && (
        <div className="flex flex-col items-center justify-center">
          <SvText
            i18nKey={
              type === "incumbent"
                ? "projections.current_mp"
                : "projections.candidate"
            }
            className="leading-5 font-semibold">
            :
          </SvText>
          <div>{party.candidate}</div>
        </div>
      )}
    </div>
  );
};
